import React from 'react'

import { observer } from 'mobx-react'
import { Layer, Source } from 'react-map-gl/mapbox'

import { ITileset } from '~/client/graph'

import { isSelfHostedTilesetId } from '../../../../utils/selfHostedTileset'
import { isTilesetDrawable } from '../../../MapBoxViewer/globeMapFiles'
import {
  MapboxSourceType,
  RASTER_TILESET_FILL_LAYER_PREFIX,
  TILESET_CIRCLE_LAYER_PREFIX,
  TILESET_FILL_LAYER_PREFIX,
  TILESET_SOURCE,
  TILESET_SYMBOL_LAYER_PREFIX,
} from '../../../MapBoxViewer/mapboxConstants'
import { TEXT_OCCLUSION_PAINT } from '../../../MapBoxViewer/mapOcclusionRule'
import {
  buildTilesetFeatureColor,
  TilesetFeatureColorProperty,
} from '../../../MapBoxViewer/tilesetFeatureColor'
import {
  TILESET_DRAW_ORDER,
  TILESET_FILL_OPACITY,
} from '../../../MapBoxViewer/tilesetFeatureStyle'
import GlobeViewTilesetLines from './GlobeViewTilesetLines'

interface IProps {
  idx: number
  tile: ITileset
  // Absent on viewers with no selection.
  selectedTilesetFeatureId?: string | number
  isHidden?: boolean
}

@observer
export default class GlobeViewTileset extends React.Component<IProps> {
  private get visibility(): 'visible' | 'none' {
    return this.props.isHidden ? 'none' : 'visible'
  }

  private get sourceUrl(): string {
    const { tile } = this.props
    return isSelfHostedTilesetId(tile.mapboxTilesetId)
      ? tile.mapboxTilesetId
      : `mapbox://${tile.mapboxData.id}`
  }

  public render(): JSX.Element {
    const { tile } = this.props
    if (!isTilesetDrawable(tile)) {
      return null
    }
    return tile.mapboxData.type === MapboxSourceType.Vector
      ? this.renderVectorLayers(tile)
      : this.renderRasterLayers(tile)
  }

  private renderRasterLayers(tile: ITileset): JSX.Element {
    const sourceLayerId = TILESET_SOURCE + tile.id
    return (
      <Source
        key={sourceLayerId}
        id={sourceLayerId}
        type={MapboxSourceType.Raster}
        url={this.sourceUrl}
        // The upload service writes the source filename into the TileJSON attribution, so mapbox
        // aggregates customer file names into the credit line; empty overrides it. A tileset needing
        // a real provider credit would have to carry it here — ITileset records no provenance.
        attribution=""
        // MTS serves 256px raster tiles and its TileJSON omits tileSize, so GL would
        // fall back to its 512 default and fetch a zoom level too shallow. STX1-1008.
        tileSize={256}
      >
        <Layer
          id={RASTER_TILESET_FILL_LAYER_PREFIX + tile.id}
          type="raster"
          source={sourceLayerId}
          layout={{ visibility: this.visibility }}
          paint={{
            'raster-fade-duration': 0,
          }}
        />
      </Source>
    )
  }

  private renderVectorLayers(tile: ITileset): JSX.Element {
    const { selectedTilesetFeatureId } = this.props
    const sourceLayerId = TILESET_SOURCE + tile.id
    return (
      <Source
        key={sourceLayerId}
        id={sourceLayerId}
        type={MapboxSourceType.Vector}
        url={this.sourceUrl}
        // Same filename-out-of-the-credit-line override as the raster source above.
        attribution=""
      >
        <Layer
          id={TILESET_FILL_LAYER_PREFIX + tile.id}
          type="fill"
          filter={['==', ['geometry-type'], 'Polygon']}
          source={sourceLayerId}
          source-layer="features"
          layout={{
            'fill-sort-key': TILESET_DRAW_ORDER,
            visibility: this.visibility,
          }}
          paint={{
            'fill-color': [
              'match',
              ['id'],
              selectedTilesetFeatureId || 0,
              '#0037ff',
              buildTilesetFeatureColor(
                TilesetFeatureColorProperty.Fill,
                '#cca329',
              ),
            ],
            'fill-outline-color': '#fcba03',
            'fill-opacity': [
              'match',
              ['id'],
              selectedTilesetFeatureId || 0,
              1,
              TILESET_FILL_OPACITY,
            ],
          }}
        />
        <GlobeViewTilesetLines
          key={tile.id}
          tileId={tile.id}
          isHidden={this.props.isHidden}
        />
        <Layer
          id={TILESET_CIRCLE_LAYER_PREFIX + tile.id}
          type="circle"
          filter={['==', ['geometry-type'], 'Point']}
          source={sourceLayerId}
          source-layer="features"
          layout={{ visibility: this.visibility }}
          paint={{
            'circle-radius': [
              'match',
              ['id'],
              selectedTilesetFeatureId || 0,
              4,
              2,
            ],
            'circle-color': [
              'match',
              ['id'],
              selectedTilesetFeatureId || 0,
              '#0037ff',
              buildTilesetFeatureColor(
                TilesetFeatureColorProperty.MarkerColor,
                '#fcba03',
              ),
            ],
            'circle-stroke-width': 0.5,
            'circle-stroke-color': 'black',
          }}
        />
        <Layer
          id={TILESET_SYMBOL_LAYER_PREFIX + tile.id}
          type="symbol"
          source={sourceLayerId}
          filter={['has', 'description']}
          source-layer="features"
          layout={{
            'text-field': ['get', 'description'],
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 10,
            'text-anchor': 'bottom',
            'text-offset': [0, 1.5],
            visibility: this.visibility,
          }}
          paint={{
            'text-color': '#fcba03',
            'text-halo-color': '#000000',
            'text-halo-width': 1,
            ...TEXT_OCCLUSION_PAINT,
          }}
        />
      </Source>
    )
  }
}
